import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchAssignedWorksheets } from "../../assignedWorksheetListSlice";

import { refinePages } from "../../../../utils/paginationUtils";
import { formatDateString, showStudentList } from "../../../../utils/textUtils";
import secret from "../../../../secret.json";

import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";

import Pagination from "../../../../components/Pagination";
import CustomCheckbox from "../../../../components/CustomCheckbox";

import WorksheetSelectBar from "../common/WorksheetSelectBar";
import WorksheetTypeBox from "../common/WorksheetTypeBox";
import WorksheetSearchBar from "../common/WorksheetSearchBar";
import DeleteWorksheetModal from "../common/DeleteWorksheetModal";
import StudentEditPage from "./StudentEditPage/StudentEditPage";
import DateUpdateModal from "./DateUpdateModal";
import PaperHomeworkGradingPage from "../../../paper-assignment/components/PaperHomeworkGradingPage";
import { CONTAINER_MAX_WIDTH } from "../../../../styles/layout";

const AssignedWorksheetTable = () => {
  const dispatch = useDispatch();

  const [isMobileW, setIsMobileW] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  useEffect(() => {
    const f = () => setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const { user } = useSelector((state) => state.auth);
  const { results, current_page, total_pages } = useSelector(
    (state) => state.assigned_worksheets
  );
  // ?type=paper 로 들어오면 지필과제 채점부터 연다(학생 제출 텔레그램 알림의 '채점하기' 링크)
  const [typeSelect, setTypeSelect] = useState(
    () => new URLSearchParams(window.location.search).get("type") || "all"
  );
  // 수학 계정은 수학 학습지·오답노트·지필과제를 고른다(기본은 전체)
  const isMathAccount = user?.site === "math";
  const [keyword, setKeyword] = useState("");
  const [selectedWorksheets, setSelectedWorksheets] = useState([]);
  const [selectedWorksheetForEdit, setSelectedWorksheetForEdit] =
    useState(null);
  const [selectedWorksheetForUpdate, setSelectedWorksheetForUpdate] =
    useState(null);
  const [showDateUpdateModal, setShowDateUpdateModal] = useState(false);

  useEffect(() => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(
      fetchAssignedWorksheets({ page: 1, type: typeParam, keyword: keyword })
    );
  }, [dispatch, typeSelect, keyword]);



  useEffect(() => {
    if (selectedWorksheetForEdit) {
      setSelectedWorksheetForEdit(
        results.find(
          (worksheet) => worksheet.id === selectedWorksheetForEdit.id
        )
      );
    }
  }, [results]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handlePageClick = (page) => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(
      fetchAssignedWorksheets({ page, type: typeParam, keyword: keyword })
    );
  };

  const handleCheckboxChange = (checked, worksheet) => {
    if (checked) {
      // 체크 O
      setSelectedWorksheets((prev) => [...prev, worksheet]);
    } else {
      // 체크 해제
      setSelectedWorksheets((prev) =>
        prev.filter((prevWorksheet) => prevWorksheet.id !== worksheet.id)
      );
    }
  };

  //   (2) 헤더(전체선택) 체크박스 핸들러
  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedWorksheets(results);
    } else {
      // 해제
      setSelectedWorksheets([]);
    }
  };

  // (3) 헤더 체크박스가 "완전히 체크" 상태인지 판별 (현재 페이지 모두 체크된 경우)
  const isHeaderChecked =
    results.length > 0 &&
    results.every((currentWorksheet) =>
      selectedWorksheets.some(
        (selectedWorksheet) => selectedWorksheet.id === currentWorksheet.id
      )
    );

  const handlePreviewClick = (assignment_list) => {
    if (assignment_list.length === 0) {
      alert("출제된 학생이 있어야, 미리보기가 가능합니다.");
      return;
    }
    const url = `${secret.STUDENT_BASE_URL}/preview?id=${assignment_list[0].id}&session=${user.session}`;
    window.open(url, "_blank");
  };

  return (
    <div
      style={{
        padding: "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      {/* 제목과 드롭다운을 같은 Row에 배치 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">출제 시험지 목록</h3>
        )}
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
          mathAssigned={isMathAccount}
        />
        <div
          style={{
            flex: isMobileW ? 1 : "none",
            width: isMobileW ? "auto" : "30%",
            minWidth: isMobileW ? 0 : "220px",
          }}
        >
          <WorksheetSearchBar
            placeholder="학습지명, 출제자 검색"
            handleFilter={setKeyword}
          />
        </div>
      </div>

      {typeSelect === "paper" ? (
        // 지필과제는 같은 날·같은 반·같은 제목을 한 줄로 묶어 보여 주고, 누르면 그 자리에서 채점한다(2026-10-05)
        <PaperHomeworkGradingPage keyword={keyword} />
      ) : (<>
      <div
        style={{
          backgroundColor: "#FFF",
          border: "1px solid #E6EDEC",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
          paddingBottom: "5px",
          overflowX: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: Math.min(window.innerWidth, window.screen.width) <= 768 ? "1000px" : undefined,
            borderCollapse: "collapse",
          }}
        >
          <colgroup>
            <col style={{ width: "48px" }} />
            <col style={{ width: "88px" }} />
            <col />
            <col style={{ width: "74px" }} />
            <col style={{ width: "82px" }} />
            <col style={{ width: "0px" }} />
            <col style={{ width: "84px" }} />
            <col style={{ width: "92px" }} />
            <col style={{ width: "168px" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1.5px solid #0E141B",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#7A8490",
              }}
            >
              <td style={{ textAlign: "center", paddingLeft: "6px" }}>
                <CustomCheckbox
                  checked={isHeaderChecked}
                  onChange={(e) => handleHeaderCheckboxChange(e.target.checked)}
                />
              </td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", whiteSpace: "nowrap" }}>문항 번호</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", whiteSpace: "nowrap" }}>학습지명</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>생성일</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>마감일</td>
              <td></td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", whiteSpace: "nowrap" }}>출제자</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>미리보기</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>출제</td>
            </tr>
            {results &&
              results.map((item, index) => {
                const isChecked = selectedWorksheets.includes(item);
                return (
                  <tr
                    key={item.id}
                    style={{
                      fontSize: "0.92rem",
                      borderBottom: "1px solid #E6EDEC",
                    }}
                  >
                    <td
                      style={{
                        textAlign: "center",
                        paddingLeft: "6px",
                      }}
                    >
                      <CustomCheckbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleCheckboxChange(e.target.checked, item)
                        }
                      />
                    </td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", color: "#7A8490", fontSize: "0.84rem", fontVariantNumeric: "tabular-nums" }}>{item.id}</td>
                    <td
                      style={{
                        padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontWeight: 700,
                        color: "#0E141B",
                      }}
                    >
                      {item.is_retry && (
                        <span style={{
                          backgroundColor: "#E8F7EE",
                          color: "#158A48",
                          padding: "3px 9px",
                          borderRadius: "7px",
                          fontSize: "0.7rem",
                          fontWeight: 800,
                          flexShrink: 0,
                        }}>
                          복습 {item.retry_info?.retry_count || ''}회차
                        </span>
                      )}
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{item.title}</span>
                      <span style={{ flexShrink: 0 }}><WorksheetTypeBox type={item.type} label={item.bank_kind_name || undefined} title={item.title} /></span>
                    </td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", color: "#3A434E", fontSize: "0.86rem", fontVariantNumeric: "tabular-nums" }}>
                      {formatDateString(item.created_at)}
                    </td>
                    <td style={{ padding: "5px", textAlign: "center" }}>
                      <div
                        style={{
                          backgroundColor: "#F4F8F7",
                          padding: "5px 14px",
                          display: "inline-block",
                          border: "1.5px solid transparent",
                          borderRadius: "999px",
                          cursor: "pointer",
                          fontSize: "0.84rem",
                          fontWeight: 700,
                          color: "#3A434E",
                          fontVariantNumeric: "tabular-nums",
                          transition: "all .12s",
                        }}
                        onClick={() => {
                          // **여는 것을 여기서 끝낸다.** 예전에는 값만 바꾸고
                          // useEffect 가 열었는데, 닫을 때 그 값을 비우지 않아
                          // 같은 줄을 다시 누르면 값이 그대로라 아무 일도 일어나지
                          // 않았다. 한 번 고친 과제의 마감일은 다시 못 고쳤다.
                          setSelectedWorksheetForUpdate(item);
                          setShowDateUpdateModal(true);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#00D9C0";
                          e.currentTarget.style.color = "#00A396";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "transparent";
                          e.currentTarget.style.color = "#3A434E";
                        }}
                        title="마감일 수정"
                      >
                        {formatDateString(item.closed_at)}
                      </div>
                    </td>
                    <td></td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", color: "#3A434E" }}>{item.issuer_name || item.author_name}</td>
                    <td style={{ position: "relative" }}>
                      <button
                        onClick={() => handlePreviewClick(item.assignment_list)}
                        style={{
                          width: "34px",
                          height: "34px",
                          backgroundColor: "#FFF",
                          border: "1.5px solid #E6EDEC",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#3A434E",
                        }}
                        title="문서 열기"
                      >
                        <DescriptionIcon style={{ fontSize: "20px" }} />
                      </button>
                    </td>
                    {/* 머리줄과 같은 좌우 여백. 없으면 학생 이름 칩이 표 오른쪽 끝에 붙는다(2026-10-18 대표). */}
                    <td style={{ textAlign: "center", padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "0 10px" : "0 16px" }}>
                      <div
                        style={{
                          maxWidth: "156px",
                          backgroundColor: "#F4F8F7",
                          border: "none",
                          borderRadius: "999px",
                          margin: "0 auto",
                          fontSize: "0.83rem",
                          fontWeight: 700,
                          color: "#3A434E",
                          padding: "5px 13px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          transition: "background-color .12s",
                        }}
                        onClick={() => {
                          setSelectedWorksheetForEdit(item);
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#E5FBF8")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#F4F8F7")}
                        title="출제 대상 수정"
                      >
                        {showStudentList({
                          student_count: item.student_count,
                          assignment_list: item.assignment_list,
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* 페이지네이션 */}
        <Pagination
          visiblePages={visiblePages}
          current_page={current_page}
          handlePageClick={handlePageClick}
        />
      </div>

      {selectedWorksheets.length > 0 && (
        <DeleteWorksheetModal
          worksheets={selectedWorksheets}
          setSelectedWorksheets={setSelectedWorksheets}
          type={"assigned"}
          onDeleted={() => {
            const typeParam = typeSelect === "all" ? null : typeSelect;
            dispatch(
              fetchAssignedWorksheets({ page: current_page, type: typeParam, keyword: keyword })
            );
          }}
        />
      )}
      </>)}
      {selectedWorksheetForEdit && (
        <StudentEditPage
          onClose={() => setSelectedWorksheetForEdit(null)}
          worksheet={selectedWorksheetForEdit}
        />
      )}
      {showDateUpdateModal && (
        <DateUpdateModal
          onClose={() => {
            setShowDateUpdateModal(false);
            setSelectedWorksheetForUpdate(null);
          }}
          worksheet={selectedWorksheetForUpdate}
        />
      )}
    </div>
  );
};

export default AssignedWorksheetTable;
